import React, { useState, useEffect } from 'react'
import { Save, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { showSuccess } from '@/lib/swal'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { accountManagementService, FinanceModule, Account, AccountSettingResponse, TransactionAccountVm, Category } from '@/services/accountManagementService'
import { useAuthStore } from '@/store/auth.store'
import { useSettingsStore } from '@/store/settings.store'

/**
 * Account Management page
 * Allows configuration of account templates for finance modules
 */
export const Accounts: React.FC = () => {
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  const branchId = user?.selectedBranch?.branchId

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  
  // Finance modules dropdown
  const [financeModules, setFinanceModules] = useState<FinanceModule[]>([])
  const [selectedModuleId, setSelectedModuleId] = useState<number | null>(null)
  
  // Accounts for debit/credit dropdowns
  const [allAccounts, setAllAccounts] = useState<Account[]>([])
  
  // Account setting data
  const [accountSetting, setAccountSetting] = useState<AccountSettingResponse | null>(null)
  
  // Form data - account templates
  const [accountTemplates, setAccountTemplates] = useState<TransactionAccountVm[]>([])

  // Load finance modules on mount
  useEffect(() => {
    if (branchId) {
      loadFinanceModules()
      loadAllAccounts()
    }
  }, [branchId])

  // Load account setting when module is selected
  useEffect(() => {
    if (selectedModuleId && branchId) {
      loadAccountSetting(selectedModuleId, branchId)
    } else {
      setAccountSetting(null)
      setAccountTemplates([])
    }
  }, [selectedModuleId, branchId])

  const loadFinanceModules = async () => {
    try {
      setLoading(true)
      const modules = await accountManagementService.getMaster()
      setFinanceModules(modules)
    } catch (error: any) {
      console.error('Error loading finance modules:', error)
      toast.error('Failed to load finance modules')
    } finally {
      setLoading(false)
    }
  }

  const loadAllAccounts = async () => {
    if (!branchId) return
    
    try {
      console.log('🔵 Loading all accounts for branchId:', branchId)
      const accounts = await accountManagementService.getAllAccounts(branchId)
      console.log('🔵 Loaded accounts:', accounts)
      setAllAccounts(accounts)
      if (accounts.length === 0) {
        console.warn('⚠️ No accounts found for branchId:', branchId)
      }
    } catch (error: any) {
      console.error('❌ Error loading accounts:', error)
      console.error('❌ Error response:', error.response)
      console.error('❌ Error data:', error.response?.data)
      toast.error(error.response?.data?.message || 'Failed to load accounts')
    }
  }

  const loadAccountSetting = async (id: number, branchId: number) => {
    try {
      setLoading(true)
      const setting = await accountManagementService.getAccountSettingById(id, branchId)
      setAccountSetting(setting)
      
      // Initialize account templates from existing data or categories
      if (setting.categories && setting.categories.length > 0) {
        const templates: TransactionAccountVm[] = setting.categories.map((category) => {
          // Find existing template for this category
          const existingTemplate = setting.accountTemplate?.find(
            (t) => t.transactionTypeIdD === category.id
          )
          
          return {
            id: existingTemplate?.id || 0,
            transactionModuleId: id,
            transactionTypeIdD: category.id,
            debitAccountId: existingTemplate?.debitAccountId || 0,
            creditAccountId: existingTemplate?.creditAccountId || 0,
            branchId: branchId,
          }
        })
        setAccountTemplates(templates)
      } else {
        setAccountTemplates([])
      }
    } catch (error: any) {
      console.error('Error loading account setting:', error)
      toast.error('Failed to load account setting')
      setAccountSetting(null)
      setAccountTemplates([])
    } finally {
      setLoading(false)
    }
  }

  const handleModuleChange = (value: string) => {
    const moduleId = value ? Number(value) : null
    setSelectedModuleId(moduleId)
  }

  const handleDebitAccountChange = (categoryId: number, accountId: string) => {
    setAccountTemplates((prev) =>
      prev.map((template) =>
        template.transactionTypeIdD === categoryId
          ? { ...template, debitAccountId: Number(accountId) || 0 }
          : template
      )
    )
  }

  const handleCreditAccountChange = (categoryId: number, accountId: string) => {
    setAccountTemplates((prev) =>
      prev.map((template) =>
        template.transactionTypeIdD === categoryId
          ? { ...template, creditAccountId: Number(accountId) || 0 }
          : template
      )
    )
  }

  // Get user IP address (logged-in user's IP, not server's IP)
  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  const handleSave = async () => {
    if (!selectedModuleId || !branchId) {
      toast.error('Please select a finance module')
      return
    }

    if (accountTemplates.length === 0) {
      toast.error('No account templates to save')
      return
    }

    // Validate that at least one account (debit or credit) is selected
    const invalidTemplates = accountTemplates.filter(
      (t) => !t.debitAccountId && !t.creditAccountId
    )

    if (invalidTemplates.length > 0) {
      toast.error('Please select at least one account (debit or credit) for each template')
      return
    }

    try {
      setSaving(true)
      
      // Get logged-in user's IP address (not server's IP)
      const ipAddress = await getUserIPAddress()
      
      // Prepare save data
      const saveData: TransactionAccountVm[] = accountTemplates.map((template) => ({
        id: template.id || 0,
        transactionModuleId: selectedModuleId,
        transactionTypeIdD: template.transactionTypeIdD,
        debitAccountId: template.debitAccountId,
        creditAccountId: template.creditAccountId,
        branchId: branchId,
        ipAddress: ipAddress, // Set logged-in user's IP address
      }))

      await accountManagementService.saveAccountSetting(saveData)
      await showSuccess('Saved', 'Account settings saved successfully.')
      
      // Reload the data
      await loadAccountSetting(selectedModuleId, branchId)
    } catch (error: any) {
      console.error('Error saving account setting:', error)
      toast.error(error.response?.data?.message || 'Failed to save account settings')
    } finally {
      setSaving(false)
    }
  }

  // Prepare finance module options
  const financeModuleOptions: SearchableSelectOption[] = financeModules.map((module) => ({
    value: String(module.id),
    label: language === 'ar' 
      ? (module.lookupNameAr || module.lookupNameEn || '') 
      : (module.lookupNameEn || module.lookupNameAr || ''),
  }))

  // Prepare account options
  const accountOptions: SearchableSelectOption[] = allAccounts.map((account) => {
    const accountId = account.id || account.accountId || 0
    const accountCode = account.accountCode || ''
    const accountName = language === 'ar'
      ? (account.accountNameAr || account.accountNameEn || account.accountName || '')
      : (account.accountNameEn || account.accountName || account.accountNameAr || '')
    
    const displayLabel = accountCode 
      ? `${accountCode} (${accountName})`
      : accountName

    return {
      value: String(accountId),
      label: displayLabel,
    }
  })

  // Get category name helper
  const getCategoryName = (category: Category): string => {
    return language === 'ar'
      ? (category.lookupNameAr || category.lookupNameEn || '')
      : (category.lookupNameEn || category.lookupNameAr || '')
  }

  // Get selected account label helper - removed unused
  // const getAccountLabel = (accountId: number): string => {
  //   if (!accountId) return ''
  //   const account = allAccounts.find((acc) => (acc.id || acc.accountId) === accountId)
  //   if (!account) return ''
  //   
  //   const accountCode = account.accountCode || ''
  //   const accountName = language === 'ar'
  //     ? (account.accountNameAr || account.accountNameEn || account.accountName || '')
  //     : (account.accountNameEn || account.accountName || account.accountNameAr || '')
  //   
  //   return accountCode ? `${accountCode} (${accountName})` : accountName
  // }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold">Account Management</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Configure account templates for finance modules
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="space-y-2 max-w-md">
              <Label>
                Finance Modules <span className="text-red-500">*</span>
              </Label>
              <SearchableSelect
                options={financeModuleOptions}
                value={selectedModuleId ? String(selectedModuleId) : ''}
                onValueChange={handleModuleChange}
                placeholder="Select"
                searchPlaceholder="Search finance modules..."
                emptyMessage="No finance modules found"
                disabled={loading}
              />
            </div>

            {accountSetting && accountSetting.categories && accountSetting.categories.length > 0 && (
              <div className="mt-8">
                <div className="px-4 py-2 rounded-t-md" style={{ backgroundColor: '#E9EDF2' }}>
                  <h3 className="font-semibold text-gray-900">Account Template</h3>
                </div>
                <div className="border border-t-0 rounded-b-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Debit Account</TableHead>
                        <TableHead>Credit Account</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {accountSetting.categories.map((category) => {
                        const template = accountTemplates.find(
                          (t) => t.transactionTypeIdD === category.id
                        )
                        
                        return (
                          <TableRow key={category.id}>
                            <TableCell>
                              <Input
                                value={getCategoryName(category)}
                                readOnly
                                className="bg-muted"
                              />
                            </TableCell>
                            <TableCell>
                              <SearchableSelect
                                options={accountOptions}
                                value={template?.debitAccountId ? String(template.debitAccountId) : ''}
                                onValueChange={(value) => handleDebitAccountChange(category.id, value)}
                                placeholder="Select debit account..."
                                searchPlaceholder="Search accounts..."
                                emptyMessage="No accounts found"
                                disabled={saving}
                              />
                            </TableCell>
                            <TableCell>
                              <SearchableSelect
                                options={accountOptions}
                                value={template?.creditAccountId ? String(template.creditAccountId) : ''}
                                onValueChange={(value) => handleCreditAccountChange(category.id, value)}
                                placeholder="Select credit account..."
                                searchPlaceholder="Search accounts..."
                                emptyMessage="No accounts found"
                                disabled={saving}
                              />
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {selectedModuleId && accountSetting && accountSetting.categories && accountSetting.categories.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No categories found for this finance module
              </div>
            )}

            {selectedModuleId && (
              <div className="flex justify-end mt-6">
                <Button
                  onClick={handleSave}
                  disabled={saving || !accountSetting || accountTemplates.length === 0}
                  className="bg-black text-white hover:bg-gray-800"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

